<template>
  <member-header :pages="pages" :page-sizes="pageSizes" :num-items="count" @search="search" />
  <button class="btn btn-primary btn-sm ms-2 custom-export-button" :disabled="isExporting" @click="exportMembers">
    <FontAwesomeIcon v-if="isExporting" icon="fa-solid fa-spinner" spin />Export
  </button>
  <div v-if="isLoading" class="table-responsive">
    <div class="loadingSpinner">
      <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
      <span>loading...</span>
    </div>
  </div>
  <div v-else class="table-responsive">
    <div class="custom-listing" :style="listingHeight">
      <z-table v-model="records" class="table table-bordered table-striped" :is-single-row-detail="false"
        :is-header-fixed="isHeaderFixed" :sort-by="sortBy" :order-by="orderBy" :columns="tableColumnsName"
        :detail-column-class="detailColumnClass" :loading="isLoading" @sort="setOrder">
        <template #tbody="props">
          <th scope="row" class="custom-column-60">{{ props.row.id }}</th>
          <td>{{ props.row.firmName }}</td>
          <td class="custom-column-160">{{ props.row.estimatingId }}</td>
          <td class="custom-column-140">{{ props.row.source }}</td>
          <td class="custom-column-120 text-center" :class="{ 'text-success fw-bold': props.row.isOptIn }">{{
            setOptIn(props.row.isOptIn) }}</td>
          <td class="custom-column-200">{{ formatDate(props.row.optInOn) }}</td>
        </template>
        <template #empty> Nothing found! </template>
      </z-table>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { locationStore } from '@/stores/location'
import { Parser } from '@json2csv/plainjs'
import globals from '@/plugins/globals'
import ZTable from '@/components/utilities/elements/Table.vue'
import MemberHeader from './MemberHeader.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import moment from 'moment'
export default {
  name: 'MemberListing',
  components: {
    ZTable,
    MemberHeader,
    FontAwesomeIcon
  },
  props: {
    listingHeight: String
  },
  data() {
    return {
      isHeaderFixed: true,
      page: 1,
      pageSizes: [
        { name: '10', value: 10 },
        { name: '25', value: 25 },
        { name: '50', value: 50 },
        { name: '100', value: 100 }
      ],
      detailColumnClass: 'custom-column-60',
      isExporting: false
    }
  },
  async mounted() {
    this.isExporting = false
    this.page = 1
    this.setListParam({
      name: 'page',
      value: this.page
    })
    await this.search()
  },
  computed: {
    ...mapState(locationStore, ['loading', 'listParams', 'location', 'members', 'membersForExport']),
    count() {
      let _count = 0
      if (this.members) {
        _count = this.members.count
      }
      return _count
    },
    records() {
      let _records = []
      if (this.members) {
        _records = this.members.items
      }
      return _records
    },
    tableColumnsName() {
      let columns = [
        {
          field: 'firmFirm',
          label: '',
          sortable: false,
          class: 'custom-column-60'
        },
        { field: 'firmName', label: 'Location', sortable: true },
        { field: 'estimatingId', label: 'Estimating Id', sortable: true, class: 'custom-column-160' },
        { field: 'source', label: 'Source', sortable: true, class: 'custom-column-140' },
        { field: 'isOptIn', label: 'Opt In?', sortable: true, class: 'custom-column-120 text-center' },
        { field: 'optInOn', label: 'Opt In Changed', sortable: true, class: 'custom-column-200' }
      ]
      return columns
    },
    pages() {
      let _pages = 0
      if (this.members) {
        _pages = Math.ceil(this.members.count / this.listParams.pageSize)
      }
      return _pages
    },
    sortBy() {
      return this.listParams.orderBy
    },
    orderBy() {
      return this.listParams.sortBy
    },
    isLoading() {
      return this.loading.members
    },
    isProvider() {
      let _is = false
      if (this.location) {
        if (this.location.firmClass === 5) {
          _is = true
        }
      }
      return _is
    }
  },
  methods: {
    ...mapActions(locationStore, ['setListParam', 'paginate', 'getMembers', 'getMembersForExport']),
    formatDate(date, type = 'datetime') {
      return globals.formatDate(date, type)
    },
    async setOrder(column, sortBy) {
      this.page = 1
      this.setListParam({
        name: 'sortBy',
        value: sortBy
      })
      this.setListParam({
        name: 'orderBy',
        value: column.field
      })
      console.log('Sort Params', {
        column: column,
        sortBy: sortBy,
        params: this.listParams
      })
      await this.search(this.page, this.searchTerm)
    },
    setOptIn(data) {
      return data ? 'Yes' : 'No'
    },
    async exportMembers() {
      this.isExporting = true
      await this.getMembersForExport({
        firm: this.location.firm,
        id: this.location.firm,
        type: 99,
        filter: 'root'
      })
      if (this.membersForExport.length > 0) {
        this.processExport()
      }
      this.isExporting = false
    },
    async processExport() {
      try {
        const parser = new Parser()
        const csv = parser.parse(this.membersForExport)
        let now = moment().format('YYYY-MM-DD')
        console.log(csv)
        // download the file as CSV
        var downloadLink = document.createElement('a')
        var blob = new Blob(['\ufeff', csv])
        var url = URL.createObjectURL(blob)
        downloadLink.href = url
        downloadLink.download = `Membership for ${this.location.firmName} ${now}.csv`
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      } catch (err) {
        console.error(err)
      }
    },
    async search(page = 1, searchTerm) {
      // Load up the Members
      try {
        console.log('Search Members', this.location)
        if (this.location.firm) {
          if (searchTerm !== '' && searchTerm !== null) {
            this.page = 1
          } else {
            this.page = page
          }
          await this.getMembers({
            firm: this.location.firm,
            id: this.location.firm,
            page: this.page,
            offset: (this.page - 1) * this.listParams.pageSize,
            sortBy: this.listParams.sortBy,
            orderBy: this.listParams.orderBy,
            pageSize: this.listParams.pageSize,
            searchTerm: this.listParams.searchTerm,
            filter: 'root',
            lookup: this.isProvider ? 5 : 3
          })
        }
      } catch (e) {
        console.log('Error', e)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-listing {
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  max-height: 600px;
  height: 600px;
}

.custom-header {
  position: sticky;
  top: 0;
}

.custom-export-button {
  position: absolute;
  top: 0;
  right: 15px;
}
</style>
